import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Switch } from "./ui/switch";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api, { formatApiError } from "../lib/api";
import { getCachedThumb, fetchThumb, fetchPdfPreview } from "../lib/thumbCache";
import { formatDate } from "../lib/format";
import { modSelect, noShiftTextSelect } from "../lib/mouseSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import ConvertPdfDialog, { isPdf, PDF_CONVERTIBLE_RE } from "./ConvertPdfDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { useHoverPreviewPref } from "../lib/hoverPreview";
import {
  Paperclip, Loader2, Folder, FolderPlus, File as FileIcon, Image as ImageIcon, Film, FileText,
  Upload, Home, ChevronRight, ChevronLeft, ExternalLink, Mail, Copy, Download, Pencil, Trash2, Search,
  LayoutGrid, Grid2x2, X, FolderInput, RotateCw, RotateCcw, Camera, Check, Share2, UserRound, CheckCircle2, ZoomIn, ZoomOut, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import CameraCaptureDialog from "./CameraCaptureDialog";
import JSZip from "jszip";
import exifr from "exifr";
import { Checkbox } from "./ui/checkbox";
import ShareListDialog from "./condivisioni/ShareListDialog";
import { UploadOptionsPanel } from "./UploadOptionsPanel";
import NameCaseButtons from "./NameCaseButtons";
import { isVideo, VideoThumb, VideoPlayer } from "./VideoPreview";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";

async function renderPdfThumb(blob) {
  try {
    const buf = await blob.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    const v = page.getViewport({ scale: 420 / vp.width });
    const canvas = document.createElement("canvas");
    canvas.width = v.width; canvas.height = v.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: v }).promise;
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch { return null; }
}

const CATEGORIE = [
  { k: "entrata", l: "Documenti in Entrata" }, { k: "uscita", l: "Documenti in Uscita" },
  { k: "avvocato", l: "Allegati Avvocato" }, { k: "perito", l: "Allegati Perito" },
  { k: "cessionario", l: "Allegati Cessionario" }, { k: "fornitore", l: "Allegati Fornitore" },
  { k: "assistito", l: "Allegati Assistito" },
];
const IMG_RE = /image\/(jpeg|jpg|png|webp)/i;

const blobToB64 = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.readAsDataURL(blob); });

async function readExifDate(file) {
  try {
    const d = await exifr.parse(file, ["DateTimeOriginal"]);
    const dt = d?.DateTimeOriginal;
    if (dt instanceof Date && !isNaN(dt.getTime())) return dt.toISOString();
    return null;
  } catch { return null; }
}

const STAMP_POS = [{ k: "br", l: "Basso dx" }, { k: "bl", l: "Basso sx" }, { k: "tr", l: "Alto dx" }, { k: "tl", l: "Alto sx" }];
const STAMP_FMTS = [{ k: "dt", l: "GG/MM/AAAA HH:MM" }, { k: "d", l: "GG/MM/AAAA" }, { k: "t", l: "HH:MM" }];
const fmtStamp = (d, fmt = "dt") => {
  const p = (n) => String(n).padStart(2, "0");
  const ds = `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`, ts = `${p(d.getHours())}:${p(d.getMinutes())}`;
  return fmt === "d" ? ds : fmt === "t" ? ts : `${ds} ${ts}`;
};

function drawStamp(ctx, w, h, stamp) {
  const fs = Math.max(14, Math.round(Math.min(w, h) * 0.045));
  const m = Math.round(fs * 0.8);
  ctx.font = `normal ${fs}px Arial, sans-serif`;
  ctx.textAlign = stamp.pos.endsWith("r") ? "right" : "left";
  ctx.textBaseline = stamp.pos.startsWith("t") ? "top" : "bottom";
  const x = stamp.pos.endsWith("r") ? w - m : m, y = stamp.pos.startsWith("t") ? m : h - m;
  ctx.shadowColor = "rgba(0,0,0,0.85)"; ctx.shadowBlur = Math.round(fs * 0.25); ctx.shadowOffsetX = 2; ctx.shadowOffsetY = 2;
  ctx.lineWidth = Math.max(2, Math.round(fs * 0.12)); ctx.strokeStyle = "rgba(0,0,0,0.7)"; ctx.strokeText(stamp.text, x, y);
  ctx.shadowBlur = 0; ctx.fillStyle = stamp.color || "#ffffff"; ctx.fillText(stamp.text, x, y);
}

async function compressImage(file, quality, stamp) {
  try {
    const q = quality === "alta" ? 0.9 : quality === "media" ? 0.6 : 0.4;
    const maxDim = quality === "alta" ? 2400 : quality === "media" ? 1600 : 1000;
    const img = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d"); ctx.drawImage(img, 0, 0, w, h);
    if (stamp) drawStamp(ctx, w, h, stamp);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", q));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.(png|webp|jpeg|jpg)$/i, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

// Anteprima ingrandita al passaggio del mouse: carica l'originale via presigned (direct-to-storage)
// e lo mostra grande senza aprire il file; fallback alla miniatura se il presigned non è disponibile.
export function HoverPreview({ pid, file, thumb }) {
  const [url, setUrl] = useState(null);
  const isImg = (file.content_type || "").startsWith("image/");
  const isPdf = (file.content_type || "").includes("pdf");
  useEffect(() => {
    let active = true;
    (async () => { const u = await fetchPdfPreview(pid, file.id); if (active) setUrl(u); })();
    return () => { active = false; };
  }, [pid, file.id]);
  if (url && isPdf) return <object data={`${url}#toolbar=0&navpanes=0&view=Fit`} type="application/pdf" aria-label={file.nome} className="h-[70vh] w-[540px] rounded" />;
  if (url && isImg) return <img src={url} alt={file.nome} className="max-h-[70vh] max-w-[540px] object-contain rounded" />;
  if (thumb) return <img src={thumb} alt={file.nome} className="w-[480px] max-h-[70vh] max-w-[540px] object-contain rounded" />;
  return <div className="h-40 w-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>;
}

function fileIcon(ct, cls) {
  if ((ct || "").startsWith("image/")) return <ImageIcon className={cls} />;
  if ((ct || "").startsWith("video/")) return <Film className={cls} />;
  if ((ct || "").includes("pdf")) return <FileText className={cls} />;
  return <FileIcon className={cls} />;
}

export default function AllegatiDialog({ open, onOpenChange, praticaId, praticaNumero, assistitoName, assistitoKey = "principale", onSendEmail, onComposeEmail, initialCategoria }) {
  const [sharesOpen, setSharesOpen] = useState(false);
  const [hoverOn, toggleHover] = useHoverPreviewPref();
  const [categoria, setCategoria] = useState("entrata");
  const [counts, setCounts] = useState({});
  const [folderId, setFolderId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("date_desc");
  const [quality, setQuality] = useState("media");
  const [stamp, setStamp] = useState({ on: false, pos: "br", fmt: "dt", color: "#ffffff" });
  const stampOn = stamp.on;
  const stampLoaded = useRef(false);
  const stampTimer = useRef(null);
  useEffect(() => {
    if (!open || stampLoaded.current) return;
    api.get("/me/stamp-prefs").then(({ data }) => { if (data) setStamp(data); stampLoaded.current = true; }).catch(() => {});
  }, [open]);
  const updStamp = (patch) => setStamp((s) => {
    const next = { ...s, ...patch };
    clearTimeout(stampTimer.current);
    stampTimer.current = setTimeout(() => api.patch("/me/stamp-prefs", next).catch(() => {}), 400);
    return next;
  });
  const [uploading, setUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState([]); // [{key,name,pct,status}]
  const cancelUploadRef = useRef(false);
  const uploadAbortRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [viewByFolder, setViewByFolder] = useState({});
  const [imgUrls, setImgUrls] = useState({});
  const [pdfUrls, setPdfUrls] = useState({});
  const [lightbox, setLightbox] = useState(-1);
  const [rotating, setRotating] = useState(false);
  const [failedThumbs, setFailedThumbs] = useState(() => new Set());
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Zoom/pan del visualizzatore foto
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const lbRef = useRef(null);
  const dragRef = useRef(null);
  const clampZoom = (z) => Math.min(5, Math.max(0.5, Math.round(z * 100) / 100));
  const applyZoom = (fn) => setZoom((z) => { const nz = clampZoom(fn(z)); if (nz <= 1) setPan({ x: 0, y: 0 }); return nz; });
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [lightbox]);
  useEffect(() => {
    const el = lbRef.current;
    if (!el || lightbox < 0) return undefined;
    const onWheel = (e) => { e.preventDefault(); applyZoom((z) => z * (e.deltaY < 0 ? 1.15 : 1 / 1.15)); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);
  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => { const d = dragRef.current; if (d) setPan({ x: d.px + e.clientX - d.sx, y: d.py + e.clientY - d.sy }); };
    const up = () => { setDragging(false); setTimeout(() => { dragRef.current = null; }, 0); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [dragging]);
  const startDrag = (e) => {
    if (zoom <= 1 || e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y };
    setDragging(true);
  };
  const [pendingFiles, setPendingFiles] = useState(null); // for "choose folder" modal
  const [optFiles, setOptFiles] = useState(null);
  const [convAsk, setConvAsk] = useState(null);
  const [convAnswers, setConvAnswers] = useState({});
  const [dupFor, setDupFor] = useState(null); // file being duplicated
  const [dupQuery, setDupQuery] = useState("");
  const [dupResults, setDupResults] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [videoFile, setVideoFile] = useState(null);
  const [selFolders, setSelFolders] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(undefined); // undefined=nessuna, null=HOME, string=folderId
  const [moveOtherOpen, setMoveOtherOpen] = useState(false);
  const [copyOnly, setCopyOnly] = useState(false); // modale aperta da "Copia in altra pratica"
  const navigate = useNavigate();
  const [copyDone, setCopyDone] = useState(null); // { id, msg } conferma copia con link alla pratica
  useEffect(() => { if (!copyDone) return undefined; const t = setTimeout(() => setCopyDone(null), 12000); return () => clearTimeout(t); }, [copyDone]);
  const [moveOtherQuery, setMoveOtherQuery] = useState("");
  const [moveOtherResults, setMoveOtherResults] = useState([]);
  const [moveOtherTarget, setMoveOtherTarget] = useState(null); // pratica di destinazione selezionata
  const [moveOtherSoggetti, setMoveOtherSoggetti] = useState([]);
  const [moveOtherSoggetto, setMoveOtherSoggetto] = useState(null); // soggetto destinazione scelto
  const fileInput = useRef(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const renameCancel = useRef(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderVal, setNewFolderVal] = useState("");
  const [folderNameSuggestions, setFolderNameSuggestions] = useState([]);
  const [folderNameListOpen, setFolderNameListOpen] = useState(false);
  const [renamingName, setRenamingName] = useState(null);

  const base = `/studio/pratiche/${praticaId}`;
  const currentFolder = folders.find((f) => f.id === folderId);
  const viewKey = folderId || "root";
  const view = viewByFolder[viewKey] || "grande";
  const setView = (v) => setViewByFolder((m) => ({ ...m, [viewKey]: v }));
  const [thumbSize, setThumbSizeState] = useState(() => localStorage.getItem("pgp_allegati_thumb") || "media");
  const setThumbSize = (v) => { setThumbSizeState(v); localStorage.setItem("pgp_allegati_thumb", v); };
  const GRID_BY_SIZE = {
    piccola: "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3",
    media: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3",
    grande: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4",
  };
  const isImage = (ct) => (ct || "").startsWith("image/");
  const hasThumbCt = (ct) => (ct || "").startsWith("image/") || (ct || "").includes("pdf");
  const fmtSize = (b) => { if (!b) return "0 B"; const u = ["B", "KB", "MB", "GB"]; let i = 0, n = b; while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; } return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`; };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fo, fi, cn] = await Promise.all([
        api.get(`${base}/allegati-folders`, { params: { assistito: assistitoKey, categoria } }),
        api.get(`${base}/allegati-files`, { params: { assistito: assistitoKey, categoria, folder_id: folderId || "" } }),
        api.get(`${base}/allegati-counts`, { params: { assistito: assistitoKey } }),
      ]);
      setFolders(fo.data || []); setFiles(fi.data || []); setCounts(cn.data || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [base, categoria, folderId, assistitoKey]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (open) { setFolderId(null); } }, [open, categoria]);
  useEffect(() => { if (open && initialCategoria) setCategoria(initialCategoria); }, [open, initialCategoria]);
  useEffect(() => {
    let active = true;
    // Prima passata SINCRONA: mostra subito le miniature già in cache (apertura istantanea).
    const fromCache = {};
    for (const a of files) {
      if (imgUrls[a.id] || !hasThumbCt(a.content_type || "")) continue;
      const c = getCachedThumb(a.id);
      if (c) fromCache[a.id] = c;
    }
    if (Object.keys(fromCache).length) setImgUrls((m) => ({ ...fromCache, ...m }));
    (async () => {
      for (const a of files) {
        if (imgUrls[a.id] || fromCache[a.id]) continue;
        const ct = a.content_type || "";
        if (!hasThumbCt(ct)) continue; // foto e PDF hanno una miniatura pre-generata
        try {
          const url = await fetchThumb(praticaId, a.id, ct);
          if (!active) return;
          if (url) setImgUrls((m) => (m[a.id] ? m : { ...m, [a.id]: url }));
          else if (ct.includes("pdf")) {
            const p = await fetchPdfPreview(praticaId, a.id);
            if (!active) return;
            if (p) setPdfUrls((m) => (m[a.id] ? m : { ...m, [a.id]: p }));
            else setFailedThumbs((s) => { const n = new Set(s); n.add(a.id); return n; });
          } else setFailedThumbs((s) => { const n = new Set(s); n.add(a.id); return n; });
        } catch { setFailedThumbs((s) => { const n = new Set(s); n.add(a.id); return n; }); }
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);
  useEffect(() => { if (!open) { setImgUrls((m) => { Object.values(m).forEach((u) => URL.revokeObjectURL(u)); return {}; }); setPdfUrls({}); setLightbox(-1); setFailedThumbs(new Set()); } }, [open]);
  // Al click esplicito (lightbox) carica l'ORIGINALE a piena risoluzione; nelle griglie resta solo la miniatura.
  useEffect(() => {
    let active = true; let obj = null;
    setLightboxUrl(null);
    const a = lightbox >= 0 ? imageFiles[lightbox] : null;
    if (a) {
      (async () => {
        try {
          // Originale caricato DIRETTAMENTE dallo storage (URL firmato), senza byte dal backend.
          const url = await getContentUrl(a);
          if (!active) return;
          if (url) { setLightboxUrl(url); return; }
          // Fallback GridFS: streaming via server.
          const b = await fetchBlob(a); if (!active) return; obj = URL.createObjectURL(b); setLightboxUrl(obj);
        } catch { /* in caso di errore resta la miniatura */ }
      })();
    }
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  const sortedFiles = useMemo(() => {
    const arr = [...files];
    arr.sort((a, b) => {
      if (sort === "name_asc") return a.nome.localeCompare(b.nome);
      if (sort === "name_desc") return b.nome.localeCompare(a.nome);
      if (sort === "date_asc") return (a.created_at || "").localeCompare(b.created_at || "");
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return arr;
  }, [files, sort]);

  const imageFiles = useMemo(() => sortedFiles.filter((a) => isImage(a.content_type)), [sortedFiles]);

  const prepareFile = async (raw) => {
    let f = raw, shot = null;
    if (IMG_RE.test(raw.type)) {
      shot = await readExifDate(raw);
      const st = stampOn ? { text: fmtStamp(shot ? new Date(shot) : new Date(), stamp.fmt), pos: stamp.pos, color: stamp.color } : null;
      f = await compressImage(raw, quality, st);
    }
    return { f, shot };
  };

  const uploadOne = async (item, raw0, targetFolder, controller, cat = categoria) => {
    const patch = (p) => setUploadItems((arr) => arr.map((x) => (x.key === item.key ? { ...x, ...p } : x)));
    let raw = raw0;
    if (item.convert) {
      patch({ status: "converting" });
      try {
        const fd = new FormData(); fd.append("file", raw0);
        const { data } = await api.post("/studio/convert-to-pdf", fd, { responseType: "blob", signal: controller.signal });
        raw = new File([data], raw0.name.replace(/\.[^.]+$/, "") + ".pdf", { type: "application/pdf", lastModified: raw0.lastModified });
        patch({ name: raw.name });
      } catch (err) {
        if (err.code === "ERR_CANCELED" || err.name === "CanceledError") { patch({ status: "canceled" }); return false; }
        toast.error(`Conversione in PDF non riuscita per "${raw0.name}": caricato nel formato originale`);
      }
    }
    if (!IMG_RE.test(raw.type) && raw.size > 60 * 1024 * 1024) {
      patch({ status: "error" }); toast.error(`"${raw.name}" supera 60MB e non è stato caricato`); return false;
    }
    const { f, shot } = await prepareFile(raw);
    if (cancelUploadRef.current) { patch({ status: "canceled" }); return false; }
    patch({ status: "uploading", pct: 0 });
    const onProg = (e) => { if (e.total) patch({ pct: Math.round((e.loaded / e.total) * 100) }); };
    // 1) Tentativo di upload diretto al bucket via URL firmato (elimina il doppio trasferimento)
    try {
      const { data: pres } = await api.post(`${base}/allegati-presign`, {
        filename: f.name, content_type: f.type || "application/octet-stream", size: f.size,
        categoria: cat, folder_id: targetFolder || "", assistito: assistitoKey,
      });
      if (pres.mode === "direct") {
        try {
          await axios.put(pres.url, f, { headers: pres.headers, signal: controller.signal, onUploadProgress: onProg });
          await api.post(`${base}/allegati-files/register`, {
            backend: pres.backend, object_key: pres.object_key, bucket: pres.bucket, nome: f.name,
            content_type: f.type || "application/octet-stream", size: f.size, categoria: cat,
            folder_id: targetFolder || "", assistito: assistitoKey, data_scatto: shot || "",
          });
          patch({ pct: 100, status: "done" }); return true;
        } catch (err) {
          if (err.code === "ERR_CANCELED" || err.name === "CanceledError") { patch({ status: "canceled" }); return false; }
          // CORS/permessi bucket non configurati → fallback all'upload via server
        }
      }
    } catch (err) {
      if (err.code === "ERR_CANCELED" || err.name === "CanceledError") { patch({ status: "canceled" }); return false; }
      // presign fallito → fallback all'upload via server
    }
    // 2) Fallback: upload via server (provider Emergent/GridFS o bucket senza CORS)
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("assistito", assistitoKey); fd.append("categoria", cat);
      fd.append("folder_id", targetFolder || "");
      if (shot) fd.append("data_scatto", shot);
      await api.post(`${base}/allegati-files`, fd, { headers: { "Content-Type": "multipart/form-data" }, signal: controller.signal, onUploadProgress: onProg });
      patch({ pct: 100, status: "done" }); return true;
    } catch (err) {
      if (err.code === "ERR_CANCELED" || err.name === "CanceledError") { patch({ status: "canceled" }); return false; }
      patch({ status: "error" }); toast.error(formatApiError(err.response?.data?.detail)); return false;
    }
  };

  const doUpload = (fileList, targetFolder, cat = categoria, conv = null) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    if (conv) { runUpload(list, targetFolder, cat, conv); return; }
    if (list.some((f) => !isPdf(f) && PDF_CONVERTIBLE_RE.test(f.name))) { setConvAnswers({}); setConvAsk({ list, targetFolder, cat }); return; }
    runUpload(list, targetFolder, cat, {});
  };

  const runUpload = async (list, targetFolder, cat, conv) => {
    cancelUploadRef.current = false;
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    const items = list.map((raw, i) => ({ key: `${Date.now()}-${i}-${raw.name}`, name: raw.name, pct: 0, status: "pending", convert: !!conv[raw.name + raw.size] }));
    setUploadItems(items);
    setUploading(true);
    const CONC = 3;
    let idx = 0, ok = 0;
    const worker = async () => {
      while (idx < list.length) {
        if (cancelUploadRef.current) break;
        const my = idx++;
        if (await uploadOne(items[my], list[my], targetFolder, controller, cat)) ok++;
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONC, list.length) }, worker));
    uploadAbortRef.current = null;
    setUploading(false);
    const cancelled = cancelUploadRef.current;
    if (cancelled) toast.info(ok ? `Caricamento annullato · ${ok} file già caricati mantenuti` : "Caricamento annullato");
    else if (ok) toast.success(`${ok} file caricati`);
    if (ok) load();
    setTimeout(() => setUploadItems([]), (cancelled || ok < list.length) ? 4000 : 1200);
  };

  const cancelUpload = () => { cancelUploadRef.current = true; uploadAbortRef.current?.abort(); };

  const onPick = (e) => { const fl = Array.from(e.target.files || []); e.target.value = ""; if (fl.length) setOptFiles({ files: fl, folder: folderId }); };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const fl = e.dataTransfer.files;
    if (!fl?.length) return;
    if (!folderId && folders.length > 0) setPendingFiles(Array.from(fl)); // ask which folder
    else setOptFiles({ files: Array.from(fl), folder: folderId });
  };

  const createFolder = async () => {
    try { const { data } = await api.get("/studio/allegato-folder-names"); setFolderNameSuggestions(data || []); }
    catch { setFolderNameSuggestions([]); }
    setNewFolderVal(""); setFolderNameListOpen(false); setNewFolderOpen(true);
  };
  const reloadFolderNames = async () => {
    try { const { data } = await api.get("/studio/allegato-folder-names"); setFolderNameSuggestions(data || []); } catch { /* */ }
  };
  const renameFolderName = async () => {
    const nn = renameVal.trim();
    if (!nn) { toast.error("Nome non valido"); return; }
    try {
      await api.patch("/studio/allegato-folder-names", { old: renamingName, new: nn });
      setRenamingName(null); await reloadFolderNames(); toast.success("Cartella rinominata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteFolderName = async (name) => {
    if (!window.confirm(`Eliminare la voce "${name}" dall'elenco cartelle predefinite?`)) return;
    try {
      await api.delete("/studio/allegato-folder-names", { params: { name } });
      await reloadFolderNames(); toast.success("Voce eliminata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitNewFolder = async () => {    const name = newFolderVal.trim();
    if (!name) { toast.error("Inserisci una descrizione"); return; }
    try {
      await api.post(`${base}/allegati-folders`, { assistito: assistitoKey, categoria, name });
      setNewFolderOpen(false); setNewFolderVal(""); setFolderNameListOpen(false); load(); toast.success("Cartella creata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delFolder = async (f, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Eliminare la cartella "${f.name}"? I file torneranno alla home.`)) return;
    try { await api.delete(`${base}/allegati-folders/${f.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fetchBlob = async (a) => (await api.get(`${base}/allegati-files/${a.id}/content`, { responseType: "blob" })).data;
  const getContentUrl = async (a) => { try { const { data } = await api.get(`${base}/allegati-files/${a.id}/content-url`); return data?.url || null; } catch { return null; } };
  const rotateImage = async (direction) => {
    const a = imageFiles[lightbox];
    if (!a || rotating) return;
    setRotating(true);
    try {
      await api.post(`${base}/allegati-files/${a.id}/rotate`, { direction });
      const b = (await api.get(`${base}/allegati-files/${a.id}/content`, { responseType: "blob" })).data;
      const u = URL.createObjectURL(b);
      setImgUrls((m) => { const old = m[a.id]; if (old) URL.revokeObjectURL(old); return { ...m, [a.id]: u }; });
      setLightboxUrl(URL.createObjectURL(b));
      toast.success("Immagine ruotata e salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rotazione non riuscita"); }
    finally { setRotating(false); }
  };
  const openFile = async (a) => { if (isVideo(a.content_type)) { setVideoFile(a); return; } const url = await getContentUrl(a); if (url) { window.open(url, "_blank"); return; } try { const b = await fetchBlob(a); window.open(URL.createObjectURL(b), "_blank"); } catch (e) { toast.error("Impossibile aprire il file"); } };
  const saveFile = async (a) => { try { const b = await fetchBlob(a); const u = URL.createObjectURL(b); const el = document.createElement("a"); el.href = u; el.download = a.nome; el.click(); URL.revokeObjectURL(u); } catch { toast.error("Download fallito"); } };
  const emailFile = async (a) => { try { const b = await fetchBlob(a); const c = await blobToB64(b); onSendEmail && onSendEmail([{ nome: a.nome, mime: a.content_type, content_b64: c }]); } catch { toast.error("Impossibile allegare il file"); } };
  const startRename = (a) => { renameCancel.current = false; setRenameVal(a.nome); setRenamingId(a.id); };
  const commitRename = async (a) => {
    if (renamingId !== a.id) return;
    setRenamingId(null);
    if (renameCancel.current) { renameCancel.current = false; return; }
    const name = renameVal.trim();
    if (!name || name === a.nome) return;
    try { await api.patch(`${base}/allegati-files/${a.id}`, { nome: name }); load(); toast.success("Nome aggiornato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteFile = async (a) => { if (!window.confirm(`Eliminare "${a.nome}"?`)) return; try { await api.delete(`${base}/allegati-files/${a.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleShareFile = async (a) => {
    const on = !a.condiviso_cliente;
    try {
      await api.post(`${base}/allegati-files/${a.id}/condivisione`, { condiviso: on });
      setFiles((fs) => fs.map((x) => (x.id === a.id ? { ...x, condiviso_cliente: on } : x)));
      toast.success(on ? "File condiviso con il cliente" : "Condivisione rimossa");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleShareFolder = async (f, e) => {
    e.stopPropagation();
    const on = !f.condivisa_cliente;
    try {
      await api.post(`${base}/allegati-folders/${f.id}/condivisione`, { condivisa: on });
      setFolders((fs) => fs.map((x) => (x.id === f.id ? { ...x, condivisa_cliente: on } : x)));
      toast.success(on ? "Cartella condivisa con il cliente" : "Condivisione cartella rimossa");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (dupFor === null) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/studio/pratiche-search", { params: { q: dupQuery } }); setDupResults((data || []).filter((p) => p.id !== praticaId)); } catch { /* */ }
    }, 250);
    return () => clearTimeout(t);
  }, [dupQuery, dupFor, praticaId]);

  const doDuplica = async (targetId) => {
    try { const { data } = await api.post(`${base}/allegati-files/${dupFor.id}/duplica`, { target_pratica_id: targetId }); toast.success(`Copiato nella pratica ${data.target}`); setDupFor(null); setDupQuery(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { setSelected(new Set()); setSelFolders(new Set()); }, [categoria, folderId]);

  const lastSelRef = useRef(null);
  const toggleFile = (id) => { lastSelRef.current = id; setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const modClick = (id) => (e) => modSelect(e, id, sortedFiles.map((f) => f.id), lastSelRef,
    (x) => setSelected((s) => { const n = new Set(s); n.has(x) ? n.delete(x) : n.add(x); return n; }),
    (ids) => setSelected((s) => new Set([...s, ...ids])));
  const toggleFolderSel = (id) => setSelFolders((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const displayedFolders = !folderId ? folders : [];
  const allSelected = (sortedFiles.length + displayedFolders.length) > 0 && sortedFiles.every((f) => selected.has(f.id)) && displayedFolders.every((f) => selFolders.has(f.id));
  const toggleSelectAll = async () => {
    if (allSelected) { setSelected(new Set()); setSelFolders(new Set()); return; }
    const ids = new Set(sortedFiles.map((f) => f.id));
    setBulkBusy(true);
    try {
      for (const fo of displayedFolders) {
        try {
          const { data } = await api.get(`${base}/allegati-files`, { params: { assistito: assistitoKey, categoria, folder_id: fo.id } });
          (data || []).forEach((f) => ids.add(f.id));
        } catch { /* noop */ }
      }
    } finally { setBulkBusy(false); }
    setSelected(ids); setSelFolders(new Set(displayedFolders.map((f) => f.id)));
  };
  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} file selezionat${ids.length === 1 ? "o" : "i"}? L'operazione non è reversibile.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`${base}/allegati-files/bulk-delete`, { ids });
      toast.success(`${data?.deleted ?? ids.length} file eliminati`);
      clearSel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };
  const selCount = selected.size + selFolders.size;
  const clearSel = () => { setSelected(new Set()); setSelFolders(new Set()); };

  const gatherSelected = async () => {
    const map = new Map();
    sortedFiles.forEach((f) => { if (selected.has(f.id)) map.set(f.id, f); });
    for (const fid of selFolders) {
      try {
        const { data } = await api.get(`${base}/allegati-files`, { params: { assistito: assistitoKey, categoria, folder_id: fid } });
        (data || []).forEach((f) => map.set(f.id, f));
      } catch { /* noop */ }
    }
    return Array.from(map.values());
  };

  const downloadSelected = async () => {
    setBulkBusy(true);
    try {
      const list = await gatherSelected();
      if (!list.length) { toast.error("Nessun file selezionato"); return; }
      if (list.length === 1) { await saveFile(list[0]); return; }
      const zip = new JSZip();
      const counts = {};
      for (const f of list) {
        const b = await fetchBlob(f);
        const orig = f.nome || "file";
        let name = orig;
        if (counts[orig] != null) {
          counts[orig] += 1;
          const dot = orig.lastIndexOf(".");
          const bn = dot > 0 ? orig.slice(0, dot) : orig;
          const ext = dot > 0 ? orig.slice(dot) : "";
          name = `${bn} (${counts[orig]})${ext}`;
        } else { counts[orig] = 0; }
        zip.file(name, b);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const u = URL.createObjectURL(blob); const el = document.createElement("a"); el.href = u; el.download = "Allegati.zip"; el.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Download selezione fallito"); }
    finally { setBulkBusy(false); }
  };

  const emailSelected = async () => {
    setBulkBusy(true);
    try {
      const list = await gatherSelected();
      if (!list.length) { toast.error("Nessun file selezionato"); return; }
      const atts = [];
      for (const f of list) { const b = await fetchBlob(f); atts.push({ nome: f.nome, mime: f.content_type, content_b64: await blobToB64(b) }); }
      onComposeEmail && onComposeEmail(atts);
    } catch { toast.error("Impossibile allegare i file"); }
    finally { setBulkBusy(false); }
  };

  const doMove = async () => {
    if (moveTarget === undefined) { toast.error("Seleziona una destinazione"); return; }
    const ids = sortedFiles.filter((f) => selected.has(f.id)).map((f) => f.id);
    if (!ids.length) { toast.error("Nessun file selezionato"); return; }
    setBulkBusy(true);
    try {
      for (const id of ids) await api.patch(`${base}/allegati-files/${id}`, { folder_id: moveTarget === null ? "" : moveTarget });
      toast.success(`${ids.length} file spostati`);
      setMoveOpen(false); setMoveTarget(undefined); clearSel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  // Sposta i file e/o le cartelle selezionati in un'altra tab (categoria) della stessa pratica.
  const moveToCategory = async (cat) => {
    const ids = sortedFiles.filter((f) => selected.has(f.id)).map((f) => f.id);
    const folderIds = Array.from(selFolders);
    if (!ids.length && !folderIds.length) { toast.error("Seleziona almeno un elemento"); return; }
    const label = (CATEGORIE.find((c) => c.k === cat) || {}).l || cat;
    setBulkBusy(true);
    try {
      for (const id of ids) await api.patch(`${base}/allegati-files/${id}`, { categoria: cat });
      for (const fid of folderIds) await api.post(`${base}/allegati-folders/${fid}/move-category`, { categoria: cat });
      const n = ids.length + folderIds.length;
      toast.success(`${n} element${n === 1 ? "o" : "i"} spostat${n === 1 ? "o" : "i"} in "${label}"`);
      clearSel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  useEffect(() => {
    if (!moveOtherOpen) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/studio/pratiche-search", { params: { q: moveOtherQuery } }); setMoveOtherResults((data || []).filter((p) => p.id !== praticaId)); } catch { /* */ }
    }, 250);
    return () => clearTimeout(t);
  }, [moveOtherQuery, moveOtherOpen, praticaId]);

  const resetMoveOtherTarget = () => { setMoveOtherTarget(null); setMoveOtherSoggetti([]); setMoveOtherSoggetto(null); };
  const closeMoveOther = () => { setMoveOtherOpen(false); resetMoveOtherTarget(); };
  const selectMoveOtherPratica = async (p) => {
    setBulkBusy(true);
    try {
      const { data } = await api.get(`/studio/pratiche/${p.id}/soggetti`);
      const clients = (data || []).filter((s) => s.assistito_key);
      setMoveOtherTarget(p);
      setMoveOtherSoggetti(clients);
      setMoveOtherSoggetto(clients.length <= 1 ? (clients[0] || null) : null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const doMoveAltraPratica = async (mode) => {
    if (!moveOtherTarget) return;
    const fileIds = sortedFiles.filter((f) => selected.has(f.id)).map((f) => f.id);
    const folderIds = Array.from(selFolders);
    if (!fileIds.length && !folderIds.length) { toast.error("Nessun elemento selezionato"); return; }
    setBulkBusy(true);
    try {
      const payload = { target_pratica_id: moveOtherTarget.id, mode, file_ids: fileIds, folder_ids: folderIds };
      if (moveOtherSoggetto?.assistito_key) payload.assistito_key = moveOtherSoggetto.assistito_key;
      const { data } = await api.post(`${base}/allegati-move-pratica`, payload);
      const msg = mode === "move" ? `${data.copied} file spostati nella pratica ${data.target}` : `${data.copied} file copiati nella pratica ${data.target}`;
      const targetId = moveOtherTarget.id;
      if (copyOnly) {
        setCopyDone({ id: targetId, msg });
      } else {
        toast.success(msg);
      }
      setMoveOtherOpen(false); resetMoveOtherTarget(); setMoveOtherQuery(""); clearSel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 flex flex-col" data-testid="allegati-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2 text-base">
            <Paperclip className="h-5 w-5 text-amber-400 shrink-0" />
            <span className="uppercase tracking-wide">Allegati per l'assistito {assistitoName || "—"} sulla pratica {praticaNumero || ""}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Tabs categorie */}
        <div className="flex flex-wrap gap-1 border-b border-slate-800 pb-2" data-testid="allegati-tabs">
          {CATEGORIE.map((c) => (
            <button key={c.k} onClick={() => setCategoria(c.k)} data-testid={`allegati-tab-${c.k}`}
              className={`text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors ${categoria === c.k ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "text-slate-400 hover:bg-slate-800/60 border border-transparent"}`}>
              {c.l.toUpperCase()} <span data-testid={`allegati-tab-count-${c.k}`} className={categoria === c.k ? "text-amber-300" : "text-slate-500"}>({counts[c.k] || 0})</span>
            </button>
          ))}
          <button onClick={() => setSharesOpen(true)} data-testid="allegati-tab-condivisioni"
            className="text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors text-sky-300 hover:bg-sky-500/10 border border-sky-500/30">
            CONDIVISIONI
          </button>
          <ShareListDialog open={sharesOpen} onOpenChange={setSharesOpen} praticaId={praticaId} />
        </div>

        {/* Breadcrumb + azioni */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-sm text-slate-300" data-testid="allegati-breadcrumb">
            <button onClick={() => setFolderId(null)} className="flex items-center gap-1 hover:text-amber-400"><Home className="h-4 w-4" /> HOME</button>
            {currentFolder && (<><ChevronRight className="h-3.5 w-3.5 text-slate-600" /><span className="text-amber-400 font-medium">{currentFolder.name}</span></>)}
            <span className="ml-2 text-[11px] text-slate-500" data-testid="allegati-count">· {sortedFiles.length} file · {fmtSize(sortedFiles.reduce((s, a) => s + (a.size || 0), 0))}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-700 overflow-hidden">
              <button onClick={() => setView("grande")} data-testid="allegati-view-grande" title="Griglia grande" className={`px-2 py-1.5 ${view === "grande" ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800"}`}><LayoutGrid className="h-4 w-4" /></button>
              <button onClick={() => setView("miniature")} data-testid="allegati-view-miniature" title="Miniature" className={`px-2 py-1.5 ${view === "miniature" ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800"}`}><Grid2x2 className="h-4 w-4" /></button>
            </div>
            <button onClick={toggleHover} data-testid="allegati-hover-toggle" title={hoverOn ? "Disattiva anteprima ingrandita al passaggio del mouse" : "Attiva anteprima ingrandita al passaggio del mouse"}
              className={`px-2 py-1.5 rounded-md border flex items-center gap-1 text-xs font-semibold ${hoverOn ? "border-amber-500 text-amber-500 bg-amber-500/10" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>
              {hoverOn ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} Anteprima
            </button>
            {view === "grande" && (
              <div className="flex rounded-md border border-slate-700 overflow-hidden" data-testid="allegati-thumb-size" title="Dimensione miniature">
                {[["piccola", "Piccola"], ["media", "Media"], ["grande", "Grande"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setThumbSize(k)} data-testid={`allegati-thumb-${k}`}
                    className={`px-2 py-1.5 text-xs font-semibold ${thumbSize === k ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800"}`}>{lbl}</button>
                ))}
              </div>
            )}
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="allegati-sort" className="h-8 w-auto bg-slate-900/60 border-slate-700 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                <SelectItem value="name_asc">Nome ↑</SelectItem><SelectItem value="name_desc">Nome ↓</SelectItem>
                <SelectItem value="date_asc">Cronologico ↑</SelectItem><SelectItem value="date_desc">Cronologico ↓</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={toggleSelectAll} disabled={bulkBusy} data-testid="allegati-select-all" className="h-8 border-slate-700 text-slate-200 hover:bg-slate-800">{allSelected ? "Deseleziona tutto" : "Seleziona tutto"}</Button>
            <Button size="sm" variant="outline" onClick={createFolder} data-testid="allegati-new-folder" className="h-8 border-slate-700 text-slate-200 hover:bg-slate-800"><FolderPlus className="h-4 w-4 mr-1" /> Cartella</Button>
            <Button size="sm" onClick={() => setCameraOpen(true)} data-testid="allegati-camera-btn" className="h-8 bg-slate-700 hover:bg-slate-600 text-white font-semibold"><Camera className="h-4 w-4 mr-1" /> SCATTA FOTO</Button>
            <Button size="sm" onClick={() => fileInput.current?.click()} data-testid="allegati-file-btn" className="h-8 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Upload className="h-4 w-4 mr-1" /> FILE</Button>
          </div>
        </div>

        {/* Qualità compressione immagini */}
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-400">
          <span>Qualità immagini:</span>
          {["alta", "media", "bassa"].map((q) => (
            <button key={q} onClick={() => setQuality(q)} data-testid={`allegati-quality-${q}`}
              className={`rounded px-2 py-0.5 border ${quality === q ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-slate-700 hover:bg-slate-800/50"}`}>{q.charAt(0).toUpperCase() + q.slice(1)}</button>
          ))}
          <span className="text-slate-600">(i video vengono caricati senza compressione, max 60MB)</span>
          <span className="w-px h-4 bg-slate-700 mx-1" />
          <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="allegati-stamp-toggle-label">
            <Switch checked={stampOn} onCheckedChange={(v) => updStamp({ on: v })} data-testid="allegati-stamp-toggle" className="scale-75 data-[state=checked]:bg-amber-500" />
            <span className={stampOn ? "text-amber-400" : ""}>Timbra data/ora</span>
          </label>
          {stampOn && STAMP_POS.map((p) => (
            <button key={p.k} onClick={() => updStamp({ pos: p.k })} data-testid={`allegati-stamp-pos-${p.k}`}
              className={`rounded px-2 py-0.5 border ${stamp.pos === p.k ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-slate-700 hover:bg-slate-800/50"}`}>{p.l}</button>
          ))}
          {stampOn && (
            <>
              <select value={stamp.fmt} onChange={(e) => updStamp({ fmt: e.target.value })} data-testid="allegati-stamp-format"
                className="h-6 rounded border border-slate-700 bg-slate-900/60 px-1.5 text-xs text-slate-200">
                {STAMP_FMTS.map((f) => <option key={f.k} value={f.k}>{f.l}</option>)}
              </select>
              <label className="flex items-center gap-1 cursor-pointer" title="Colore del testo">
                <span>Colore</span>
                <input type="color" value={stamp.color} onChange={(e) => updStamp({ color: e.target.value })} data-testid="allegati-stamp-color"
                  className="h-6 w-8 cursor-pointer rounded border border-slate-700 bg-transparent p-0" />
              </label>
            </>
          )}
        </div>

        {/* Griglia file/cartelle con drop */}
        <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
          data-testid="allegati-grid"
          className={`flex-1 overflow-y-auto rounded-lg border-2 border-dashed p-3 min-h-[200px] ${dragOver ? "border-amber-500 bg-amber-500/5" : "border-slate-800"}`}>
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : (
            <div className={view === "grande" ? (GRID_BY_SIZE[thumbSize] || GRID_BY_SIZE.media) : "grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 gap-2"}>
              {!folderId && folders.map((f) => (
                <div key={f.id} onDoubleClick={() => setFolderId(f.id)} data-testid={`allegati-folder-${f.id}`}
                  className="group relative flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-slate-800/50 cursor-pointer">
                  <div className="absolute top-1 left-1 z-10" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selFolders.has(f.id)} onCheckedChange={() => toggleFolderSel(f.id)} data-testid={`allegati-folder-check-${f.id}`} className="bg-slate-900/80 border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                  </div>
                  <Folder className={view === "grande" ? "h-14 w-14 text-amber-400" : "h-9 w-9 text-amber-400"} />
                  <span className="text-xs text-slate-200 text-center truncate w-full" title={f.name}>{f.name}</span>
                  <span className="text-[9px] text-slate-500" data-testid={`allegati-folder-stats-${f.id}`}>{f.file_count || 0} file · {fmtSize(f.total_size || 0)}</span>
                  <button onClick={(e) => delFolder(f, e)} data-testid={`allegati-folder-del-${f.id}`} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => toggleShareFolder(f, e)} onDoubleClick={(e) => e.stopPropagation()} data-testid={`allegati-folder-share-${f.id}`}
                    title={f.condivisa_cliente ? "Condivisa con il cliente — clic per rimuovere" : "Condividi cartella con il cliente"}
                    className={`absolute top-6 right-1 ${f.condivisa_cliente ? "opacity-100 text-emerald-500" : "opacity-0 group-hover:opacity-100 text-slate-400 hover:text-emerald-500"}`}><Share2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {sortedFiles.map((a) => {
                const thumb = imgUrls[a.id];
                const pdfPreview = pdfUrls[a.id];
                const isImg = isImage(a.content_type);
                const isPdf = (a.content_type || "").includes("pdf");
                const isVid = isVideo(a.content_type);
                const onThumbClick = () => (isImg ? setLightbox(imageFiles.findIndex((x) => x.id === a.id)) : openFile(a));
                return (
                  <div key={a.id} onContextMenu={(e) => e.preventDefault()} data-testid={`allegati-file-${a.id}`}
                    onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect}
                    className="group relative flex flex-col items-center gap-1 rounded-lg hover:bg-slate-800/50 p-2">
                    <div className="absolute top-1 left-1 z-10" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleFile(a.id)} data-testid={`allegati-file-check-${a.id}`} className="bg-slate-900/80 border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                    </div>
                    {view === "grande" ? (
                      (isImg || isPdf) ? (
                        <HoverCard openDelay={250} closeDelay={80} {...(hoverOn ? {} : { open: false })}>
                          <HoverCardTrigger asChild>
                            <button onClick={onThumbClick} data-testid={`file-thumb-${a.id}`} className="relative w-full aspect-square rounded-md overflow-hidden bg-slate-900 flex items-center justify-center">
                              {thumb ? <img src={thumb} alt={a.nome} className="w-full h-full object-cover" /> : (isPdf && pdfPreview) ? <object data={`${pdfPreview}#toolbar=0&navpanes=0&view=FitH`} type="application/pdf" aria-label={a.nome} className="w-full h-full pointer-events-none" /> : failedThumbs.has(a.id) ? <div className="flex flex-col items-center gap-1 text-slate-600" title="Anteprima non disponibile"><ImageIcon className="h-6 w-6" /><span className="text-[8px]">n/d</span></div> : <Loader2 className="h-5 w-5 animate-spin text-slate-500" />}
                              {isPdf && a.pdf_pages ? <span data-testid={`pdf-pages-${a.id}`} className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/75 text-[9px] font-semibold text-white flex items-center gap-0.5" title={`${a.pdf_pages} pagine`}><FileText className="h-2.5 w-2.5" />{a.pdf_pages}</span> : null}
                            </button>
                          </HoverCardTrigger>
                          {(thumb || (isPdf && pdfPreview)) && (
                            <HoverCardContent side="right" align="center" className="w-auto p-1 bg-slate-950 border-slate-700 shadow-2xl">
                              <HoverPreview pid={praticaId} file={a} thumb={thumb} />
                            </HoverCardContent>
                          )}
                        </HoverCard>
                      ) : isVid ? (
                        <button onClick={onThumbClick} data-testid={`file-thumb-${a.id}`} className="relative w-full aspect-square rounded-md overflow-hidden bg-slate-900"><VideoThumb pid={praticaId} a={a} /></button>
                      ) : (
                        <div className="w-full aspect-square rounded-md bg-slate-900/60 flex items-center justify-center">{fileIcon(a.content_type, "h-12 w-12 text-slate-300")}</div>
                      )
                    ) : (
                      isVid ? (
                        <button onClick={onThumbClick} className="h-10 w-10 rounded overflow-hidden bg-slate-900"><VideoThumb pid={praticaId} a={a} iconClass="h-5 w-5" /></button>
                      ) : (isImg || isPdf) && (thumb || pdfPreview) ? (
                        <button onClick={onThumbClick} className="h-10 w-10 rounded overflow-hidden bg-slate-900">{thumb ? <img src={thumb} alt={a.nome} className="h-full w-full object-cover" /> : <object data={`${pdfPreview}#toolbar=0&navpanes=0&view=FitH`} type="application/pdf" aria-label={a.nome} className="h-full w-full pointer-events-none" />}</button>
                      ) : fileIcon(a.content_type, "h-9 w-9 text-slate-300")
                    )}
                    {renamingId === a.id ? (
                      <div className="w-full space-y-0.5" onClick={(e) => e.stopPropagation()}>
                      <Input noUppercase autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } else if (e.key === "Escape") { renameCancel.current = true; e.currentTarget.blur(); } }}
                        onBlur={() => commitRename(a)} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
                        data-testid={`file-rename-input-${a.id}`} className="h-6 text-xs bg-slate-900 border-amber-500/60 text-slate-100 px-1 w-full" />
                      <div className="flex justify-center"><NameCaseButtons small value={renameVal} onChange={setRenameVal} testId={`file-rename-case-${a.id}`} /></div>
                      </div>
                    ) : (
                      <span onDoubleClick={(e) => { e.stopPropagation(); startRename(a); }} data-testid={`file-name-${a.id}`}
                        className={`${view === "grande" ? "text-xs" : "text-[10px]"} text-slate-200 text-center break-all line-clamp-2 cursor-text`} title={`${a.nome} — doppio click per rinominare`}>{a.nome}</span>
                    )}
                    {view === "grande" && <span className="text-[9px] text-slate-500" title={a.data_scatto ? "Data scatto (EXIF)" : "Data caricamento"}>{fmtSize(a.size)} · {formatDate(a.data_scatto || a.created_at)}</span>}
                    {a.caricato_da_cliente && <span className="absolute left-1 top-8 z-10 text-[9px] font-semibold text-white bg-sky-600 shadow rounded px-1.5 py-0.5 flex items-center gap-0.5 pointer-events-none" title="File caricato dal cliente dall'Area Riservata" data-testid={`file-client-badge-${a.id}`}><UserRound className="h-2.5 w-2.5" />Caricato dal cliente</span>}
                    {a.condiviso_cliente && <span className="text-[9px] font-semibold text-emerald-500 flex items-center gap-0.5" data-testid={`file-shared-badge-${a.id}`}><Share2 className="h-2.5 w-2.5" />Condiviso</span>}
                    <div className="absolute inset-x-0 top-1 flex justify-center gap-1 opacity-0 group-hover:opacity-100 bg-popover/95 border border-border shadow-sm rounded py-1">
                      <button title="Apri" onClick={() => openFile(a)} data-testid={`file-open-${a.id}`} className="text-cyan-500 hover:text-cyan-400"><ExternalLink className="h-3.5 w-3.5" /></button>
                      <button title="Invia via email" onClick={() => emailFile(a)} data-testid={`file-email-${a.id}`} className="text-popover-foreground hover:text-amber-500"><Mail className="h-3.5 w-3.5" /></button>
                      <button title="Duplica in altra pratica" onClick={() => { setDupFor(a); setDupQuery(""); setDupResults([]); }} data-testid={`file-dup-${a.id}`} className="text-popover-foreground hover:text-amber-500"><Copy className="h-3.5 w-3.5" /></button>
                      <button title="Salva sul dispositivo" onClick={() => saveFile(a)} data-testid={`file-save-${a.id}`} className="text-popover-foreground hover:text-amber-500"><Download className="h-3.5 w-3.5" /></button>
                      <button title="Rinomina" onClick={() => startRename(a)} data-testid={`file-rename-${a.id}`} className="text-popover-foreground hover:text-amber-500"><Pencil className="h-3.5 w-3.5" /></button>
                      <button title={a.condiviso_cliente ? "Condiviso con il cliente — clic per rimuovere" : "Condividi con il cliente"} onClick={() => toggleShareFile(a)} data-testid={`file-share-${a.id}`} className={a.condiviso_cliente ? "text-emerald-500 hover:text-emerald-400" : "text-popover-foreground hover:text-emerald-500"}><Share2 className="h-3.5 w-3.5" /></button>
                      <button title="Elimina" onClick={() => deleteFile(a)} data-testid={`file-del-${a.id}`} className="text-red-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                );
              })}
              {!loading && folders.length === 0 && sortedFiles.length === 0 && (
                <div className="col-span-full text-center text-sm text-slate-500 py-8">Nessun file. Trascina qui i documenti o usa i pulsanti FILE / SCANNER.</div>
              )}
            </div>
          )}
        </div>

        {selCount > 0 && (
          <div className="flex items-center gap-3 flex-wrap border-t border-amber-500/30 bg-amber-500/5 -mx-1 px-3 py-2 rounded-lg" data-testid="allegati-selection-bar">
            <span className="text-sm text-amber-300 font-medium" data-testid="allegati-selection-count">{selCount} selezionati</span>
            <Button size="sm" onClick={downloadSelected} disabled={bulkBusy} data-testid="allegati-sel-download" className="h-8 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Scarica selezionati</Button>
            <Button size="sm" onClick={emailSelected} disabled={bulkBusy} data-testid="allegati-sel-email" className="h-8 bg-slate-700 hover:bg-slate-600 text-white"><Mail className="h-4 w-4 mr-1" /> Invia via email</Button>
            <Button size="sm" onClick={() => { if (!selected.size) { toast.error("Seleziona almeno un file"); return; } setMoveTarget(undefined); setMoveOpen(true); }} disabled={bulkBusy} data-testid="allegati-sel-move" className="h-8 bg-slate-700 hover:bg-slate-600 text-white"><FolderInput className="h-4 w-4 mr-1" /> Sposta in…</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={bulkBusy || selCount === 0} data-testid="allegati-sel-move-cat" className="h-8 bg-slate-700 hover:bg-slate-600 text-white"><FolderInput className="h-4 w-4 mr-1" /> Sposta in tab…</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#121824] border-slate-700 text-slate-100">
                {CATEGORIE.filter((c) => c.k !== categoria).map((c) => (
                  <DropdownMenuItem key={c.k} onClick={() => moveToCategory(c.k)} data-testid={`allegati-move-cat-${c.k}`} className="cursor-pointer focus:bg-slate-800 focus:text-white">{c.l}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" onClick={() => { if (!selCount) { toast.error("Seleziona almeno un elemento"); return; } resetMoveOtherTarget(); setMoveOtherQuery(""); setMoveOtherResults([]); setCopyOnly(false); setMoveOtherOpen(true); }} disabled={bulkBusy} data-testid="allegati-sel-move-pratica" className="h-8 bg-slate-700 hover:bg-slate-600 text-white"><ExternalLink className="h-4 w-4 mr-1" /> Sposta in altra pratica</Button>
            <Button size="sm" onClick={() => { if (!selCount) { toast.error("Seleziona almeno un elemento"); return; } resetMoveOtherTarget(); setMoveOtherQuery(""); setMoveOtherResults([]); setCopyOnly(true); setMoveOtherOpen(true); }} disabled={bulkBusy} data-testid="allegati-sel-copy-pratica" className="h-8 bg-slate-700 hover:bg-slate-600 text-white"><Copy className="h-4 w-4 mr-1" /> Copia in altra pratica</Button>
            {selected.size > 0 && (
              <Button size="sm" onClick={deleteSelected} disabled={bulkBusy} data-testid="allegati-sel-delete" className="h-8 bg-red-600 hover:bg-red-700 text-white"><Trash2 className="h-4 w-4 mr-1" /> Elimina selezionati</Button>
            )}
            <Button size="sm" variant="outline" onClick={clearSel} data-testid="allegati-sel-cancel" className="h-8 border-slate-700 text-slate-300 hover:bg-slate-800"><X className="h-4 w-4 mr-1" /> Annulla selezione</Button>
          </div>
        )}

        {/* Pannello avanzamento upload (per-file) */}
        {uploadItems.length > 0 && (
          <div className="border border-slate-800 rounded-lg p-2.5 max-h-40 overflow-y-auto space-y-1.5" data-testid="allegati-upload-progress">
            <div className="flex items-center justify-between">
              <span className="text-xs text-amber-400 font-medium" data-testid="allegati-upload-progress-count">
                Caricamento {uploadItems.filter((i) => i.status === "done").length} / {uploadItems.length} file
              </span>
              {uploading && (
                <Button size="sm" variant="outline" onClick={cancelUpload} data-testid="allegati-upload-cancel" className="h-6 border-red-500/50 text-red-400 hover:bg-red-500/10 text-[11px] px-2"><X className="h-3 w-3 mr-1" /> Annulla</Button>
              )}
            </div>
            {uploadItems.map((it) => (
              <div key={it.key} data-testid={`upload-item-${it.key}`} className="flex items-center gap-2 text-[11px]">
                <span className="truncate flex-1 text-slate-300" title={it.name}>{it.name}</span>
                <div className="w-28 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${it.status === "error" ? "bg-red-500" : it.status === "canceled" ? "bg-slate-500" : it.status === "done" ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${it.pct}%` }} />
                </div>
                <span className={`w-12 text-right tabular-nums ${it.status === "error" ? "text-red-400" : it.status === "done" ? "text-emerald-400" : "text-slate-400"}`}>
                  {it.status === "error" ? "errore" : it.status === "canceled" ? "—" : it.status === "converting" ? "PDF…" : `${it.pct}%`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Barra in fondo */}
        <div className="flex items-center gap-3 border-t border-slate-800 pt-3">
          <span className="text-xs uppercase tracking-wider text-slate-400 font-mono">Aggiungere allegati da:</span>
          <Button size="sm" onClick={() => fileInput.current?.click()} data-testid="allegati-bottom-file" className="h-8 bg-slate-700 hover:bg-slate-600 text-white"><Upload className="h-4 w-4 mr-1" /> FILE</Button>
          <Button size="sm" onClick={() => setCameraOpen(true)} data-testid="allegati-bottom-camera" className="h-8 bg-slate-700 hover:bg-slate-600 text-white"><Camera className="h-4 w-4 mr-1" /> SCATTA FOTO</Button>
        </div>

        <input ref={fileInput} type="file" multiple className="hidden" onChange={onPick} />

        <CameraCaptureDialog
          open={cameraOpen}
          onOpenChange={setCameraOpen}
          onCapture={(file) => doUpload([file], folderId)}
        />

        {/* Lightbox immagini */}
        {videoFile && <VideoPlayer pid={praticaId} file={videoFile} onClose={() => setVideoFile(null)} />}
        {lightbox >= 0 && imageFiles[lightbox] && (
          <div ref={lbRef} className="absolute inset-0 z-30 bg-black/90 flex items-center justify-center overflow-hidden" data-testid="allegati-lightbox" onClick={() => { if (!dragRef.current) setLightbox(-1); }}>
            <button onClick={(e) => { e.stopPropagation(); setLightbox(-1); }} data-testid="lightbox-close" className="absolute top-3 right-3 text-white hover:text-amber-400"><X className="h-6 w-6" /></button>
            <div className="absolute top-3 left-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => rotateImage("ccw")} disabled={rotating} data-testid="lightbox-rotate-ccw" title="Ruota 90° antiorario" className="p-2 rounded-full bg-black/50 text-white hover:bg-amber-500 hover:text-[#0a0d14] disabled:opacity-40 transition-colors">{rotating ? <Loader2 className="h-5 w-5 animate-spin" /> : <RotateCcw className="h-5 w-5" />}</button>
              <button onClick={() => rotateImage("cw")} disabled={rotating} data-testid="lightbox-rotate-cw" title="Ruota 90° orario" className="p-2 rounded-full bg-black/50 text-white hover:bg-amber-500 hover:text-[#0a0d14] disabled:opacity-40 transition-colors">{rotating ? <Loader2 className="h-5 w-5 animate-spin" /> : <RotateCw className="h-5 w-5" />}</button>
              <span className="w-px h-6 bg-white/20 mx-1" />
              <button onClick={() => applyZoom((z) => z - 0.25)} disabled={zoom <= 0.5} data-testid="lightbox-zoom-out" title="Rimpicciolisci" className="p-2 rounded-full bg-black/50 text-white hover:bg-amber-500 hover:text-[#0a0d14] disabled:opacity-40 transition-colors"><ZoomOut className="h-5 w-5" /></button>
              <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} data-testid="lightbox-zoom-reset" title="Ripristina 100%" className="min-w-[3.5rem] px-2 py-1 rounded-full bg-black/50 text-white text-xs font-mono hover:bg-slate-700">{Math.round(zoom * 100)}%</button>
              <button onClick={() => applyZoom((z) => z + 0.25)} disabled={zoom >= 5} data-testid="lightbox-zoom-in" title="Ingrandisci" className="p-2 rounded-full bg-black/50 text-white hover:bg-amber-500 hover:text-[#0a0d14] disabled:opacity-40 transition-colors"><ZoomIn className="h-5 w-5" /></button>
            </div>
            <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + imageFiles.length) % imageFiles.length); }} data-testid="lightbox-prev" className="absolute left-3 text-white hover:text-amber-400"><ChevronLeft className="h-8 w-8" /></button>
            {lightboxUrl || imgUrls[imageFiles[lightbox].id] ? (
              <img src={lightboxUrl || imgUrls[imageFiles[lightbox].id]} alt={imageFiles[lightbox].nome} onClick={(e) => e.stopPropagation()} onMouseDown={startDrag} draggable={false} data-testid="lightbox-image"
                style={{ imageOrientation: "from-image", transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transition: dragging ? "none" : "transform 120ms ease-out", cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "default" }}
                className="max-h-[85%] max-w-[85%] object-contain rounded select-none" />
            ) : (
              <div className="text-slate-300 text-sm flex flex-col items-center gap-2" data-testid="lightbox-unavailable" onClick={(e) => e.stopPropagation()}><ImageIcon className="h-10 w-10 text-slate-500" /> {failedThumbs.has(imageFiles[lightbox].id) ? "Anteprima non disponibile" : "Caricamento…"}</div>
            )}
            <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % imageFiles.length); }} data-testid="lightbox-next" className="absolute right-3 text-white hover:text-amber-400"><ChevronRight className="h-8 w-8" /></button>
            <div className="absolute bottom-4 text-white text-sm bg-black/50 px-3 py-1 rounded">{imageFiles[lightbox].nome} · {lightbox + 1}/{imageFiles.length}</div>
          </div>
        )}

        {/* Modale: nuova cartella con combobox descrizioni */}
        {newFolderOpen && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 rounded-lg" data-testid="allegati-new-folder-modal">
            <div className="bg-popover border border-border rounded-lg p-4 w-96 flex flex-col text-popover-foreground">
              <div className="text-sm font-semibold mb-2">Nome nuova cartella</div>
              <div className="relative">
                <Input autoFocus value={newFolderVal}
                  onChange={(e) => { setNewFolderVal(e.target.value); setFolderNameListOpen(true); }}
                  onFocus={() => setFolderNameListOpen(true)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNewFolder(); } }}
                  placeholder="Seleziona o digita una descrizione…" data-testid="new-folder-input"
                  className="bg-slate-900/60 border-slate-700 h-9" autoComplete="off" />
                {folderNameListOpen && folderNameSuggestions.filter((s) => s.toLowerCase().includes(newFolderVal.toLowerCase())).length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-popover shadow-xl max-h-48 overflow-y-auto" data-testid="new-folder-suggestions">
                    {folderNameSuggestions.filter((s) => s.toLowerCase().includes(newFolderVal.toLowerCase())).map((s, i) => (
                      <div key={s} className="flex items-center gap-1 border-b border-border last:border-0 pr-1.5" data-testid={`folder-name-row-${i}`}>
                        {renamingName === s ? (
                          <div className="flex items-center gap-1 w-full px-2 py-1.5">
                            <Input value={renameVal} autoFocus onChange={(e) => setRenameVal(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); renameFolderName(); } if (e.key === "Escape") { e.preventDefault(); setRenamingName(null); } }}
                              data-testid={`folder-name-rename-input-${i}`} className="h-7 bg-slate-900/60 border-slate-700 text-sm" autoComplete="off" />
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); renameFolderName(); }} title="Salva" data-testid={`folder-name-rename-save-${i}`} className="p-1 text-emerald-400 hover:text-emerald-300"><Check className="h-4 w-4" /></button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); setRenamingName(null); }} title="Annulla" data-testid={`folder-name-rename-cancel-${i}`} className="p-1 text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
                          </div>
                        ) : (
                          <>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); setNewFolderVal(s); setFolderNameListOpen(false); }} data-testid={`new-folder-suggestion-${i}`}
                              className="flex-1 text-left px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground rounded">{s}</button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); setRenamingName(s); setRenameVal(s); }} title="Rinomina" data-testid={`folder-name-edit-${i}`} className="p-1.5 text-slate-400 hover:text-amber-400"><Pencil className="h-3.5 w-3.5" /></button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); deleteFolderName(s); }} title="Elimina" data-testid={`folder-name-del-${i}`} className="p-1.5 text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">Le descrizioni digitate vengono salvate per riutilizzarle in seguito.</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={submitNewFolder} data-testid="new-folder-confirm" className="flex-1 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><FolderPlus className="h-4 w-4 mr-1" /> Crea</Button>
                <Button size="sm" variant="outline" onClick={() => { setNewFolderOpen(false); setFolderNameListOpen(false); }} data-testid="new-folder-cancel" className="flex-1 border-slate-700 text-slate-300">Annulla</Button>
              </div>
            </div>
          </div>
        )}

        {/* Modale: scegli cartella per drop in home */}
        {pendingFiles && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 rounded-lg" data-testid="allegati-choose-folder">
            <div className="bg-white border border-slate-300 rounded-lg p-4 w-80 max-h-[70%] overflow-y-auto text-[#333]">
              <div className="text-sm font-semibold mb-2 text-[#333]">In quale cartella inserire i file?</div>
              <button onClick={() => { setOptFiles({ files: pendingFiles, folder: null }); setPendingFiles(null); }} className="w-full text-left p-2 rounded hover:bg-slate-100 text-sm flex items-center gap-2 text-[#333]"><Home className="h-4 w-4 text-slate-600" /> HOME (nessuna cartella)</button>
              {folders.map((f) => (
                <button key={f.id} onClick={() => { setOptFiles({ files: pendingFiles, folder: f.id }); setPendingFiles(null); }} className="w-full text-left p-2 rounded hover:bg-slate-100 text-sm flex items-center gap-2 text-[#333]"><Folder className="h-4 w-4 text-amber-500" /> {f.name}</button>
              ))}
              <Button size="sm" variant="outline" onClick={() => setPendingFiles(null)} className="mt-2 w-full border-slate-300 text-[#333] hover:bg-slate-100">Annulla</Button>
            </div>
          </div>
        )}

        {convAsk && (
          <ConvertPdfDialog files={convAsk.list} answers={convAnswers} setAnswers={setConvAnswers}
            onCancel={() => setConvAsk(null)}
            onConfirm={() => { const c = convAsk; setConvAsk(null); runUpload(c.list, c.targetFolder, c.cat, convAnswers); }} />
        )}
        {optFiles && (
          <UploadOptionsPanel files={optFiles.files} defaultCategoria={categoria} categorie={CATEGORIE}
            onCancel={() => setOptFiles(null)}
            onDirect={(fl, cv) => { const o = optFiles; setOptFiles(null); doUpload(fl, o.folder, categoria, cv); }}
            onConfirm={(fl, cat, cv) => { const o = optFiles; setOptFiles(null); doUpload(fl, cat === categoria ? o.folder : null, cat, cv); }} />
        )}

        {/* Modale: duplica in altra pratica */}
        {dupFor && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 rounded-lg" data-testid="allegati-dup-modal">
            <div className="bg-[#121824] border border-slate-700 rounded-lg p-4 w-96 max-h-[80%] flex flex-col">
              <div className="text-sm font-semibold mb-2">Duplica "{dupFor.nome}" in un'altra pratica</div>
              <div className="relative mb-2"><Search className="h-4 w-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" /><Input autoFocus value={dupQuery} onChange={(e) => setDupQuery(e.target.value)} placeholder="Cerca pratica…" data-testid="dup-search" className="pl-8 bg-slate-900/60 border-slate-700 h-9" /></div>
              <div className="flex-1 overflow-y-auto space-y-1">
                {dupResults.map((p) => (
                  <button key={p.id} onClick={() => doDuplica(p.id)} data-testid={`dup-pratica-${p.id}`} className="w-full text-left p-2 rounded border border-slate-800 hover:border-amber-500/40 hover:bg-slate-800/50 text-sm">
                    <span className="font-mono text-amber-400">{p.numero_pratica}</span> <span className="text-slate-300">· {p.nome_assistito || ""}</span>
                  </button>
                ))}
                {dupResults.length === 0 && <p className="text-xs text-slate-500 py-4 text-center">Digita per cercare una pratica.</p>}
              </div>
              <Button size="sm" variant="outline" onClick={() => setDupFor(null)} className="mt-2 border-slate-700 text-slate-300">Annulla</Button>
            </div>
          </div>
        )}

        {/* Modale: sposta file in cartella / root */}
        {moveOpen && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 rounded-lg" data-testid="allegati-move-modal">
            <div className="bg-popover text-popover-foreground border border-border rounded-lg p-4 w-80 max-h-[75%] flex flex-col shadow-xl">
              <div className="text-sm font-semibold mb-2">Sposta {selected.size} file in…</div>
              <div className="flex-1 overflow-y-auto space-y-1">
                {folderId && (
                  <button onClick={() => setMoveTarget(null)} data-testid="move-target-home" className={`w-full text-left p-2 rounded text-sm flex items-center gap-2 border ${moveTarget === null ? "border-amber-500 bg-amber-500/10 text-amber-600" : "border-border text-foreground hover:bg-muted"}`}><Home className="h-4 w-4" /> HOME / Radice</button>
                )}
                {folders.filter((f) => f.id !== folderId).map((f) => (
                  <button key={f.id} onClick={() => setMoveTarget(f.id)} data-testid={`move-target-${f.id}`} className={`w-full text-left p-2 rounded text-sm flex items-center gap-2 border ${moveTarget === f.id ? "border-amber-500 bg-amber-500/10 text-amber-600" : "border-border text-foreground hover:bg-muted"}`}><Folder className="h-4 w-4 text-amber-500" /> {f.name}</button>
                ))}
                {folders.filter((f) => f.id !== folderId).length === 0 && !folderId && (
                  <p className="text-xs text-muted-foreground py-4 text-center">Nessuna cartella disponibile. Crea prima una cartella.</p>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={doMove} disabled={bulkBusy || moveTarget === undefined} data-testid="move-confirm" className="flex-1 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FolderInput className="h-4 w-4 mr-1" />} Sposta</Button>
                <Button size="sm" variant="outline" onClick={() => { setMoveOpen(false); setMoveTarget(undefined); }} data-testid="move-cancel" className="flex-1 border-border text-foreground hover:bg-muted">Annulla</Button>
              </div>
            </div>
          </div>
        )}

        {copyDone && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-20 z-30 flex items-center gap-3 rounded-lg border border-emerald-500/50 bg-[#0f1a17] px-4 py-2.5 shadow-2xl text-sm" data-testid="copy-pratica-done">
            <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
            <span className="text-emerald-200">{copyDone.msg}</span>
            <button onClick={() => { const id = copyDone.id; setCopyDone(null); onOpenChange(false); navigate(`/app/pratiche?p=${id}`); }} data-testid="copy-pratica-open-target" className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1">Apri pratica</button>
            <button onClick={() => setCopyDone(null)} data-testid="copy-pratica-done-close" className="text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Modale: sposta file/cartelle in un'altra pratica */}
        {moveOtherOpen && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 rounded-lg" data-testid="allegati-move-pratica-modal">
            <div className="bg-[#121824] border border-slate-700 rounded-lg p-4 w-[26rem] max-h-[82%] flex flex-col text-slate-100">
              <div className="text-sm font-semibold mb-2" data-testid="move-pratica-title">{copyOnly ? "Copia" : "Sposta"} {selCount} element{selCount === 1 ? "o" : "i"} in un'altra pratica</div>
              {!moveOtherTarget ? (
                <>
                  <div className="relative mb-2"><Search className="h-4 w-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" /><Input autoFocus value={moveOtherQuery} onChange={(e) => setMoveOtherQuery(e.target.value)} placeholder="Cerca per numero pratica, cliente o targa…" data-testid="move-pratica-search" className="pl-8 bg-slate-900/60 border-slate-700 h-9" /></div>
                  <div className="flex-1 overflow-y-auto space-y-1">
                    {moveOtherResults.map((p) => (
                      <button key={p.id} onClick={() => selectMoveOtherPratica(p)} disabled={bulkBusy} data-testid={`move-pratica-result-${p.id}`} className="w-full text-left p-2 rounded border border-slate-800 hover:border-amber-500/40 hover:bg-slate-800/50 text-sm disabled:opacity-50">
                        <span className="font-mono text-amber-400">{p.numero_pratica}</span> <span className="text-slate-300">· {p.nome_assistito || ""}</span>
                        {p.targa ? <span className="text-slate-500"> · {p.targa}</span> : null}
                      </button>
                    ))}
                    {moveOtherResults.length === 0 && <p className="text-xs text-slate-500 py-4 text-center">Digita per cercare una pratica.</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setMoveOtherOpen(false)} data-testid="move-pratica-cancel" className="mt-2 border-slate-700 text-slate-300">Annulla</Button>
                </>
              ) : (moveOtherSoggetti.length > 1 && !moveOtherSoggetto) ? (
                <>
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 mb-3 text-sm">
                    <div className="text-slate-400 text-xs mb-0.5">Pratica di destinazione</div>
                    <span className="font-mono text-amber-400">{moveOtherTarget.numero_pratica}</span> <span className="text-slate-200">· {moveOtherTarget.nome_assistito || ""}</span>
                    <button onClick={resetMoveOtherTarget} data-testid="move-pratica-change" className="ml-2 text-xs text-slate-400 underline hover:text-slate-200">cambia</button>
                  </div>
                  <div className="text-xs text-slate-400 mb-2">Questa pratica ha più soggetti. Scegli in quale {copyOnly ? "copiare" : "spostare"} i file:</div>
                  <div className="flex-1 overflow-y-auto rounded-md border border-slate-700 bg-[#0d111a]" data-testid="move-pratica-soggetti">
                    {moveOtherSoggetti.map((s) => (
                      <button key={s.key} onClick={() => setMoveOtherSoggetto(s)} data-testid={`move-pratica-soggetto-${s.key}`} className="w-full text-left px-3 py-2.5 hover:bg-slate-800/60 border-b border-slate-800 last:border-0 flex items-center gap-2">
                        <span className="shrink-0 text-[10px] font-bold uppercase rounded px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300">{s.tipo_label}</span>
                        <span className="min-w-0 flex-1">
                          <span className="text-slate-100 text-sm block truncate">{s.nome}</span>
                          {s.ruolo ? <span className="text-[11px] text-slate-400">{s.ruolo}</span> : null}
                        </span>
                      </button>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={closeMoveOther} data-testid="move-pratica-soggetto-cancel" className="mt-2 border-slate-700 text-slate-300">Annulla</Button>
                </>
              ) : (
                <>
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 mb-3 text-sm" data-testid="move-pratica-selected">
                    <div className="text-slate-400 text-xs mb-0.5">Pratica di destinazione</div>
                    <span className="font-mono text-amber-400">{moveOtherTarget.numero_pratica}</span> <span className="text-slate-200">· {moveOtherTarget.nome_assistito || ""}</span>
                    <button onClick={resetMoveOtherTarget} data-testid="move-pratica-change" className="ml-2 text-xs text-slate-400 underline hover:text-slate-200">cambia</button>
                  </div>
                  {moveOtherSoggetto && (
                    <div className="text-xs text-slate-300 mb-3 flex flex-wrap items-center gap-x-2" data-testid="move-pratica-soggetto-selected">
                      <span className="text-slate-500">Soggetto:</span>
                      <span className="text-slate-100">{moveOtherSoggetto.nome}{moveOtherSoggetto.ruolo ? ` — ${moveOtherSoggetto.ruolo}` : ""}</span>
                      {moveOtherSoggetti.length > 1 && <button onClick={() => setMoveOtherSoggetto(null)} data-testid="move-pratica-soggetto-back" className="text-slate-400 underline hover:text-slate-200">cambia</button>}
                    </div>
                  )}
                  {copyOnly ? (
                    <>
                      <Button size="sm" onClick={() => doMoveAltraPratica("copy")} disabled={bulkBusy} data-testid="copy-pratica-confirm" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold justify-start">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Copy className="h-4 w-4 mr-2" />} Copia nella pratica</Button>
                      <p className="text-[11px] text-slate-500 mt-2">I file vengono copiati nella stessa categoria di provenienza; le cartelle selezionate vengono ricreate nella destinazione. Gli originali restano in questa pratica.</p>
                    </>
                  ) : (<>
                  <div className="flex flex-col gap-2">
                    <Button size="sm" onClick={() => doMoveAltraPratica("copy")} disabled={bulkBusy} data-testid="move-pratica-copy" className="bg-slate-700 hover:bg-slate-600 text-white justify-start">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Copy className="h-4 w-4 mr-2" />} Sposta e mantieni copia</Button>
                    <Button size="sm" onClick={() => doMoveAltraPratica("move")} disabled={bulkBusy} data-testid="move-pratica-move" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold justify-start">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderInput className="h-4 w-4 mr-2" />} Sposta e rimuovi</Button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">I file vengono copiati nella stessa categoria di provenienza; le cartelle selezionate vengono ricreate nella destinazione. "Sposta e rimuovi" li elimina dalla pratica di origine.</p>
                  </>)}
                  <Button size="sm" variant="outline" onClick={closeMoveOther} data-testid="move-pratica-close" className="mt-3 border-slate-700 text-slate-300">Annulla</Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
